/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
import { ExcelExportParams, IRowNode } from '@ag-grid-community/core';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { AsyncPipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { memo } from 'helpful-decorators';
import { isEqual } from 'lodash-es';
import { BehaviorSubject, combineLatest, firstValueFrom, of, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  first,
  map,
  pairwise,
  shareReplay,
  startWith,
  switchMap,
} from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';

import {
  CompareBudgetVersion,
  CompareGridData,
} from '@widgets/compare-budget/compare-budget.component';
import { CompareBudgetService } from '@widgets/compare-budget/services/compare-budget.service';

import { BeActivitiesAttributesModalRowData } from '@features/inline-budget/be-activities-attributes-modal/be-activities-attributes-modal.model';
import { BeInlineCategoryDropdownOption } from '@features/inline-budget/be-inline-category-dropdown/be-inline-category-dropdown.model';
import { injectOpenActivitiesModal } from '@features/inline-budget/inline-budget-modal';
import { ProgressTrackerComponent } from '@features/progress-tracker/progress-tracker.component';
import { fetchInProgressEvents } from '@features/progress-tracker/utils/fetch-in-progress-events';

import { injectListOrganizationsWithTotalsQuery } from '@models/organization/list-organizations-with-totals.query';

import { ButtonComponent } from '@shared/components/button/button.component';
import {
  AuxButtonGroup,
  ButtonGroupComponent,
} from '@shared/components/button-group/button-group.component';
import { CheckboxComponent } from '@shared/components/checkbox/checkbox.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { ConfirmationModalComponent } from '@shared/components/modals/confirmation-modal/confirmation-modal.component';
import { CustomOverlayRef } from '@shared/components/overlay/custom-overlay-ref';
import { TablePanelComponent } from '@shared/components/table-panel/table-panel.components';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { ROUTING_PATH } from '@shared/constants/routingPath';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { ApiService } from '@shared/services/api.service';
import { ChangeOrderSharedService } from '@shared/services/change-order-shared.service';
import {
  Activity,
  ActivitySubType,
  ActivityType,
  ApprovalType,
  AuxBudgetCategoryData,
  BudgetActivityAttributes,
  BudgetType,
  CategoryType,
  ChangeOrderStatus,
  Currency,
  EntityType,
  EventType,
  GqlService,
  ListActivitiesWithInvoiceMappingsInput,
  listMissingChangeOrderActivitiesQuery,
  PermissionType,
  TemplateType,
  WorkflowStep,
} from '@shared/services/gql.service';
import { LaunchDarklyService } from '@shared/services/launch-darkly.service';
import { LfuService } from '@shared/services/lfu.service';
import { OverlayService } from '@shared/services/overlay.service';
import { AuthQuery } from '@shared/store/auth/auth.query';
import { AuthService } from '@shared/store/auth/auth.service';
import { EventTrackerService } from '@shared/store/event/event-tracker.service';
import { EventQuery } from '@shared/store/event/event.query';
import { EventService } from '@shared/store/event/event.service';
import { MainQuery } from '@shared/store/main/main.query';
import { MainStore } from '@shared/store/main/main.store';
import { OrganizationQuery } from '@shared/store/organization/organization.query';
import { OrganizationService } from '@shared/store/organization/organization.service';
import { OrganizationStore } from '@shared/store/organization/organization.store';
import { WorkflowQuery } from '@shared/store/workflow/workflow.query';
import { WorkflowService } from '@shared/store/workflow/workflow.service';
import { Utils } from '@shared/utils/utils';

import { ConfirmationWithReasonModalComponent } from '../../components/confirmation-with-reason.component';
import { ChangeOrderUploadComponent } from '../change-order/change-order-upload/change-order-upload.component';
import { ChangeOrderQuery } from '../change-order/state/change-order.query';
import { ChangeOrderService } from '../change-order/state/change-order.service';
import { ChangeOrderModel } from '../change-order/state/change-order.store';
import { ChangeOrderApprovedDialogComponent } from './components/change-order-approved-dialog/change-order-approved-dialog.component';
import { injectChangeOrderBudgetUploadModal } from './components/change-order-budget-upload/change-order-budget-upload-modal.component';
import { ChangeOrderGridComponent } from './components/compare-budget/change-order-grid.component';

interface ActivityWithAttributes extends Omit<Activity, '__typename'> {
  activity_sub_type?: ActivitySubType;
  category_full_path: string;
}

const mapSelectedRowToModalData = (node: IRowNode): Partial<BeActivitiesAttributesModalRowData> => {
  const data = node.data as CompareGridData;

  if (data.to_activity_no) {
    if (data.activity_type === 'ACTIVITY_DISCOUNT') {
      const unit_cost = data.to_row.contract_unit_cost || 0;
      const unit_num = data.to_row.unit_num || 0;

      if (unit_cost > 0) {
        return {
          display_label: data.to_display_label,
          unit_cost,
          unit_num,
          uom: 'Percentage',
        };
      }

      return {
        display_label: data.to_display_label,
        unit_cost: data.to_total_cost,
        unit_num: 1,
        uom: data.to_uom,
      };
    }

    return {
      display_label: data.to_display_label,
      unit_cost: data.to_unit_cost,
      unit_num: data.to_unit,
      uom: data.to_uom,
    };
  }

  return {};
};

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    RouterLink,
    IconComponent,
    ButtonComponent,
    NgClass,
    TooltipDirective,
    ProgressTrackerComponent,
    CheckboxComponent,
    FormsModule,
    TablePanelComponent,
    ButtonGroupComponent,
    ChangeOrderGridComponent,
  ],
  providers: [CompareBudgetService],
  styles: [
    `
      canvas {
        width: 100%;
      }
    `,
  ],
  templateUrl: './change-order-detail.component.html',
})
export class ChangeOrderDetailComponent {
  public authQuery = inject(AuthQuery);

  private apiService = inject(ApiService);
  private authService = inject(AuthService);
  private changeOrderQuery = inject(ChangeOrderQuery);
  private changeOrderService = inject(ChangeOrderService);
  private changeOrderSharedService = inject(ChangeOrderSharedService);
  private compareBudgetService = inject(CompareBudgetService);
  private readonly destroyRef = inject(DestroyRef);
  private eventQuery = inject(EventQuery);
  private eventService = inject(EventService);
  private eventTrackerService = inject(EventTrackerService);
  private gqlService = inject(GqlService);
  private launchDarklyService = inject(LaunchDarklyService);
  private lfuService = inject(LfuService);
  private mainQuery = inject(MainQuery);
  private mainStore = inject(MainStore);
  private organizationQuery = inject(OrganizationQuery);
  private organizationService = inject(OrganizationService);
  private organizationStore = inject(OrganizationStore);
  private overlayService = inject(OverlayService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private workflowQuery = inject(WorkflowQuery);
  private workflowService = inject(WorkflowService);

  activitiesModal = injectOpenActivitiesModal();
  changeOrderBudgetUploadModal = injectChangeOrderBudgetUploadModal();
  vendorsQuery = injectListOrganizationsWithTotalsQuery({
    enabled: signal(true),
  });

  changeOrder$ = new BehaviorSubject<ChangeOrderModel | null>(null);

  changeOrder = toSignal(this.changeOrder$, { initialValue: null });

  fromBudgetVersion$ = new BehaviorSubject<CompareBudgetVersion | null>(null);

  fromBudgetVersion = toSignal(this.fromBudgetVersion$, { initialValue: null });

  organization$ = this.changeOrder$.pipe(
    map((co) => co?.organization_id),
    filter((x) => !!x),
    distinctUntilChanged(),
    switchMap((id) => this.organizationQuery.selectEntity(id)),
    distinctUntilChanged(isEqual),
    shareReplay(1)
  );

  organization = toSignal(this.organization$, { initialValue: null });

  editButtonDisabledTooltip = 'Change Order must be in Pending Review to Edit';

  rowSelection = toSignal(
    this.changeOrder$.pipe(
      map((co) => {
        return {
          disabled: co?.change_order_status !== 'STATUS_PENDING_REVIEW',
          disabledTooltip: this.editButtonDisabledTooltip,
        };
      })
    ),
    {
      initialValue: {
        disabled: false,
        disabledTooltip: '',
      },
    }
  );

  toBudgetVersion$ = new BehaviorSubject<CompareBudgetVersion | null>(null);

  toBudgetVersion = toSignal(this.toBudgetVersion$, { initialValue: null });

  grid = viewChild(ChangeOrderGridComponent);

  analyticsCard = computed(() => {
    const grid = this.grid();
    const toBudgetVersion = this.toBudgetVersion();
    const organization = this.organization();

    if (!grid || !toBudgetVersion || !organization) {
      return {
        co_count_activities_with_increased_cost: 0,
        co_count_activities_with_increased_units: 0,
        variance_total_cost_AC: '',
      };
    }

    const budgetData = grid.budgetData();

    return untracked(() => {
      const diffs = this.compareBudgetService.getCompareDiffs(budgetData || []);
      const org_currency = organization.currency || Currency.USD;

      return {
        co_count_activities_with_increased_cost: diffs.count_activities_with_increased_cost,
        co_count_activities_with_increased_units: diffs.count_activities_with_increased_units,
        variance_total_cost_AC: Utils.currencyFormatter(
          diffs.variance_total_cost,
          {},
          org_currency
        ),
      };
    });
  });

  inlineEditProcessing = this.eventQuery.selectProcessingEvent(EventType.INLINE_EDIT_CHANGE_ORDER);

  editButtonsDisabled = computed(() => {
    const grid = this.grid();
    const inlineProcessing = this.inlineEditProcessing();

    if (inlineProcessing || !grid) {
      return true;
    }

    return grid.selectedRowCount() === 0;
  });

  activityModalRowMap = async (rows: BeActivitiesAttributesModalRowData[]) => {
    const { data, success } = await firstValueFrom(
      this.gqlService.listActivitiesWithInvoiceMappings$(<ListActivitiesWithInvoiceMappingsInput>{
        vendor_id: this.activitiesModal.vendor_id(),
      })
    );

    const activitiesWithoutMapping = success && data ? data : null;

    if (activitiesWithoutMapping?.length) {
      rows.forEach((row) => {
        row.deletable =
          row.deletable &&
          !activitiesWithoutMapping?.find(
            (val) => val.activity_id === row.id && val.activity_name === row.activity_name
          );
      });
    }

    return rows;
  };

  editOptions = computed<AuxButtonGroup[]>(() => {
    const editButtonsDisabled = this.editButtonsDisabled();
    const grid = this.grid();
    const budgetData = grid?.budgetData() || [];

    return untracked(() => {
      const { mapSelectedRowsToModalData, openModal } = this.activitiesModal;

      return [
        {
          iconName: 'Plus',
          name: 'Add New Activities',
          onClick: () => {
            const ref = openModal({
              allBudgetActivities: budgetData,
              budgetData,
              editMode: false,
              isAttributesEditable: false,
              notEditableAttributesTooltip: 'Functionality not supported in Change Order',
              rows: [],
              showNotesSection: false,
            });

            if (ref) {
              void this.onModalClose(ref);
            }
          },
        },
        {
          disabled: editButtonsDisabled,
          disabledTooltip: 'Select at least 1 activity to enable this option',
          iconName: 'Edit',
          name: 'Edit Activities',
          onClick: async () => {
            const ref = openModal({
              allBudgetActivities: budgetData,
              budgetData,
              isAttributesEditable: false,
              notEditableAttributesTooltip: 'Functionality not supported in Change Order',
              rows: await this.activityModalRowMap(
                mapSelectedRowsToModalData(grid!.gridAPI()!, mapSelectedRowToModalData)
              ),
              showNotesSection: false,
            });

            if (ref) {
              void this.onModalClose(ref);
            }
          },
        },
        {
          disabled: editButtonsDisabled,
          disabledTooltip: 'Select at least 1 activity to enable this option',
          iconName: 'Hierarchy3',
          name: 'Edit Attributes',
          onClick: async () => {
            const ref = openModal({
              allBudgetActivities: budgetData,
              budgetData,
              isActivityTabVisible: false,
              isAttributesEditable: false,
              notEditableAttributesTooltip: 'Functionality not supported in Change Order',
              rows: await this.activityModalRowMap(
                mapSelectedRowsToModalData(grid!.gridAPI()!, mapSelectedRowToModalData)
              ),
              showNotesSection: false,
            });

            if (ref) {
              void this.onModalClose(ref);
            }
          },
        },
      ];
    });
  });

  doNotHavePermissionMessage = MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION;
  hasUploadCOPermission = this.authService.$isAuthorized({
    permissions: [PermissionType.PERMISSION_CHANGE_ORDER_UPLOAD_BUDGET],
  });

  kebabOptions = computed<AuxButtonGroup[]>(() => {
    const organization = this.organization();
    const changeOrder = this.changeOrder();
    const grid = this.grid();
    const fromBudgetVersion = this.fromBudgetVersion();
    const trial = this.mainQuery.getSelectedTrial();

    if (!organization || !changeOrder || !trial) {
      return [];
    }

    const isPendingReview =
      changeOrder.change_order_status === ChangeOrderStatus.STATUS_PENDING_REVIEW;

    return [
      {
        disabled: !fromBudgetVersion,
        iconClass: 'text-aux-blue-dark',
        iconName: 'FileExport',
        name: 'Export',
        onClick: () => {
          grid?.onExport(
            `ChangeOrder-${changeOrder.change_order_no}-${organization.name}-${trial.name}.xlsx`
          );
        },
      },
      {
        iconClass: 'text-aux-blue-dark',
        iconName: 'Download',
        name: 'Download Change Order Supporting Documents',
        onClick: () => {
          void this.onDownloadCO();
        },
      },
      {
        iconClass: 'text-aux-blue-dark',
        iconName: 'Download',
        name: 'Download Current Budget',
        onClick: () => {
          void this.onDownloadLREBudget();
        },
      },
      {
        disabled: !isPendingReview || !this.hasUploadCOPermission(),
        disabledTooltip: !isPendingReview
          ? 'Change Order must be in Pending Review to Upload'
          : this.doNotHavePermissionMessage,
        iconClass: 'text-aux-blue-dark',
        iconName: 'Upload',
        name: 'Upload Change Order Template',
        onClick: () => {
          void this.onReplaceBudget();
        },
      },
    ];
  });

  budgetLoading$ = new BehaviorSubject(false);

  loading$ = combineLatest([this.changeOrderQuery.selectLoading(), this.budgetLoading$]).pipe(
    map((arr) => arr.some((x) => x))
  );
  primaryBudgetId$ = combineLatest([this.changeOrder$, this.organization$]).pipe(
    map(([co, org]) => {
      return (
        co?.approved_budget_version?.budget_version_id ||
        this.organizationQuery.getPrimaryBudgetVersion(org?.id || '')?.budget_version_id
      );
    })
  );

  activitiesWithMissedMapping = '';
  approvedBy$ = this.changeOrder$.pipe(
    map((co) => {
      if (
        !co ||
        (co.change_order_status !== ChangeOrderStatus.STATUS_DECLINED &&
          co.change_order_status !== ChangeOrderStatus.STATUS_APPROVED)
      ) {
        return '';
      }

      const approval = co.approvals?.[0];

      const userFormat =
        this.changeOrderSharedService.userFormatter(approval?.aux_user_id || '') || '';
      const time = Utils.dateFormatter(approval?.approval_time || '');

      return `by ${userFormat} on ${time}`;
    })
  );
  btnLoading$ = new BehaviorSubject<
    | 'admin-review'
    | 'approval'
    | 'approve'
    | 'decline'
    | 'delete'
    | 'download'
    | 'lre-download'
    | 'replace'
    | 'upload'
    | false
  >(false);
  buttonGroupOverlayPosition: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
    },
  ];
  changeOrderBudgetId$ = new BehaviorSubject<null | string>(null);
  changeOrderBudgetTemplateUploadProgressId = fetchInProgressEvents(
    EventType.CHANGE_ORDER_BUDGET_TEMPLATE_UPLOADED,
    this.destroyRef
  );
  changeOrdersLink = `/${ROUTING_PATH.BUDGET.INDEX}/${ROUTING_PATH.BUDGET.CHANGE_ORDER.INDEX}`;
  coId$ = new BehaviorSubject('');
  readonly disableSendForApprovalButtonTooltip =
    'Change Order Budget must be entered to Send for Approval';
  fetchChangeOrderBudget$ = new Subject<boolean>();
  hasMissingActivitiesWithActuals = false;
  hasMissingActivitiesWithMapping = false;
  hasUpdateChangeOrderPermissions$ = this.authService.isAuthorized$({
    permissions: [PermissionType.PERMISSION_UPDATE_CHANGE_ORDER],
    sysAdminsOnly: false,
  });
  hasUploadCOPermission$ = this.authService.isAuthorized$({
    permissions: [PermissionType.PERMISSION_CHANGE_ORDER_UPLOAD_BUDGET],
    sysAdminsOnly: false,
  });
  invoicesWithMissedMapping = '';
  isApproveButtonDisable$ = this.toBudgetVersion$.pipe(map((toBudgetVersion) => !toBudgetVersion));
  isApproveButtonDisableManually$ = new BehaviorSubject(false);
  isApproved$ = this.statusCheck(ChangeOrderStatus.STATUS_APPROVED);
  isChangeOrdersWorkflowLocked = this.workflowQuery.getLockStatusByWorkflowStepType(
    WorkflowStep.WF_STEP_MONTH_CLOSE_LOCK_CHANGE_ORDERS
  );
  isDeclined$ = this.statusCheck(ChangeOrderStatus.STATUS_DECLINED);
  isPendingApproval$ = this.statusCheck(ChangeOrderStatus.STATUS_PENDING_APPROVAL);
  isPendingReview$ = this.statusCheck(ChangeOrderStatus.STATUS_PENDING_REVIEW);
  isUploadCOBudgetButtonDisable$ = this.fromBudgetVersion$.pipe(
    map((fromBudgetVersion) => !fromBudgetVersion)
  );
  readonly messagesConstants = MessagesConstants;
  readonly missingActivitiesWithActualsSendForApprovalTooltip =
    'Template uploaded is missing activities with actuals. Re-upload with missing activities to Send for Approval.';
  readonly missingActivitiesWithActualsTooltip =
    'Template uploaded is missing activities with actuals. Re-upload with missing activities to approve.';
  readonly missingMappedActivitiesWithActualsSendForApprovalTooltip =
    'Template uploaded is missing activities with invoices mapped and activities with actuals. Re-upload with missing activities to Send for Approval.';
  readonly missingMappedActivitiesWithActualsTooltip =
    'Template uploaded is missing activities with invoices mapped and activities with actuals. Re-upload with missing activities to approve.';
  readonly missingWithMappingActivitiesSendForApprovalTooltip =
    'Template uploaded is missing activities with invoices mapped. Re-upload with missing activities to Send for Approval.';
  readonly missingWithMappingActivitiesTooltip =
    'Template uploaded is missing activities with invoices mapped. Re-upload with missing activities to approve.';
  showApprovalReminderLink$ = this.launchDarklyService.select$(
    (flags) => flags.change_order_resend_reminder_link
  );
  status$ = this.changeOrder$.pipe(
    map((changeOrder) => {
      let status = '';

      if (changeOrder) {
        switch (changeOrder.change_order_status) {
          case ChangeOrderStatus.STATUS_APPROVED:
            status = 'Approved';
            break;
          case ChangeOrderStatus.STATUS_DECLINED:
            status = 'Declined';
            break;
          case ChangeOrderStatus.STATUS_PENDING_APPROVAL:
            status = 'Pending Approval';
            break;
          case ChangeOrderStatus.STATUS_PENDING_REVIEW:
            status = 'Pending Review';
            break;
          default:
            break;
        }
      }

      return status;
    })
  );
  userHasApprovePermission$ = this.authService.isAuthorized$({
    permissions: [PermissionType.PERMISSION_APPROVE_CHANGE_ORDER],
    sysAdminsOnly: false,
  });
  zeroHyphen = Utils.zeroHyphen;

  acPercentFormat(v: number) {
    return Utils.agPercentageFormatterAbsolute(
      {
        value: v,
      },
      { maximumFractionDigits: 0, minimumFractionDigits: 0 }
    );
  }

  approveBtnTooltip(
    userHasApprovePermission: boolean,
    isChangeOrdersWorkflowLocked: boolean,
    isApproveDisabled: boolean
  ): string[] {
    if (this.hasMissingActivitiesWithActuals && this.hasMissingActivitiesWithMapping) {
      return [this.missingMappedActivitiesWithActualsTooltip];
    }

    if (this.hasMissingActivitiesWithActuals) {
      return [this.missingActivitiesWithActualsTooltip];
    }

    if (this.hasMissingActivitiesWithMapping) {
      return [this.missingWithMappingActivitiesTooltip];
    }

    if (isApproveDisabled) {
      return ['Change Order Budget must be entered to Approve'];
    }

    if (!userHasApprovePermission) {
      return [MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION];
    }

    if (isChangeOrdersWorkflowLocked) {
      return [MessagesConstants.LOCKED_FOR_PERIOD_CLOSE];
    }

    return [];
  }

  constructor() {
    this.route.paramMap
      .pipe(
        map((params) => params.get('id')),
        switchMap((id) => {
          // trying to catch an issue where the query param ?trial= is included in the param 'id' value on very rare occassions
          let verifiedId = id;

          this.coId$.next(verifiedId || '');

          if (verifiedId?.includes('?')) {
            verifiedId = verifiedId.substring(0, verifiedId.indexOf('?'));
          } else if (verifiedId?.includes('%')) {
            verifiedId = verifiedId.substring(0, verifiedId.indexOf('%'));
          }

          return this.changeOrderService.getOne(verifiedId || '').pipe(
            switchMap(({ data, success }) => {
              if (success && data) {
                return this.changeOrderQuery.selectEntity(verifiedId);
              }

              return of(null);
            })
          );
        }),
        takeUntilDestroyed()
      )
      .subscribe((changeOrder) => {
        this.organizationStore.setActive(changeOrder?.organization_id || '');

        if (!changeOrder) {
          void this.router.navigateByUrl(
            `${ROUTING_PATH.BUDGET.INDEX}/${ROUTING_PATH.BUDGET.CHANGE_ORDER.INDEX}`
          );

          return;
        }

        this.changeOrder$.next(changeOrder || null);
      });

    combineLatest([this.changeOrderBudgetId$, this.changeOrder$])
      .pipe(
        map(([id, co]) => {
          if (!(!!id && !!co)) {
            return null;
          }

          return {
            budget_name: `Change Order ${co?.change_order_no}`,
            budget_type: BudgetType.BUDGET_CHANGE_ORDER,
            budget_version_id: id,
          } as CompareBudgetVersion;
        }),
        startWith({} as CompareBudgetVersion),
        pairwise(),
        filter(([prev, next]) => !isEqual(prev, next)),
        takeUntilDestroyed()
      )
      .subscribe(([, data]) => {
        this.toBudgetVersion$.next(data);

        if (data) {
          this.listMissingChangeOrderActivities(data.budget_version_id);
        }
      });

    combineLatest([this.changeOrder$, this.organization$])
      .pipe(
        map(([co]) => {
          return this.organizationQuery.getBudgetVersion(
            co?.organization_id || '',
            BudgetType.BUDGET_CHANGE_ORDER,
            co?.id || '',
            EntityType.CHANGE_ORDER
          )?.budget_version_id;
        }),
        takeUntilDestroyed()
      )
      .subscribe((data) => {
        this.changeOrderBudgetId$.next(data as string);
      });

    this.primaryBudgetId$
      .pipe(
        filter((id) => !!id),
        map((id) => {
          return {
            budget_name: 'Current Budget',
            budget_type: BudgetType.BUDGET_PRIMARY,
            budget_version_id: id,
          } as CompareBudgetVersion;
        }),
        takeUntilDestroyed()
      )
      .subscribe((data) => {
        this.fromBudgetVersion$.next(data);
      });

    combineLatest([
      this.fetchChangeOrderBudget$.pipe(startWith(true)),
      toObservable(this.inlineEditProcessing).pipe(
        startWith(false),
        filter((s) => this.changeOrderBudgetId$.getValue() == null && s === false)
      ),
    ])
      .pipe(
        switchMap(() => this.organizationService.getListWithTotalBudgetAmount()),
        takeUntilDestroyed()
      )
      .subscribe();

    effect(() => {
      const isProcessing = this.eventQuery.selectProcessingEvent(
        EventType.INLINE_EDIT_CHANGE_ORDER
      )();

      const isBudgetUploadProcessing = this.eventQuery.selectProcessingEvent(
        EventType.CHANGE_ORDER_BUDGET_TEMPLATE_UPLOADED
      )();

      if (isProcessing === false || isBudgetUploadProcessing === false) {
        this.activitiesModal.refreshCategories();
      }
    });

    this.workflowService.getWorkflowList().pipe(takeUntilDestroyed()).subscribe();

    combineLatest([this.organization$, this.changeOrder$, this.toBudgetVersion$])
      .pipe(debounceTime(500), takeUntilDestroyed())
      .subscribe(([org, co, v]) => {
        if (org && co) {
          this.activitiesModal.vendor_id.set(org.id);
          this.activitiesModal.change_order_id.set(v ? co.id : undefined);
        }
      });
  }

  deleteBtnTooltip(isChangeOrdersWorkflowLocked: boolean): string {
    if (isChangeOrdersWorkflowLocked) {
      return MessagesConstants.LOCKED_FOR_PERIOD_CLOSE;
    }

    return '';
  }

  getCategoryPreviousFullPath(
    category: BeInlineCategoryDropdownOption,
    trialVendorPath: string
  ): string {
    const originalCategory = this.activitiesModal
      .categories()
      ?.find((origCategory) => origCategory.id === category.id);

    return originalCategory ? `${trialVendorPath}${originalCategory.fullPath}` : '';
  }

  getDynamicExcelParams(): ExcelExportParams {
    const org = this.organizationQuery.getEntity(this.changeOrder$.value?.organization_id);

    const currentTrial = this.mainQuery.getSelectedTrial();

    const changeOrderNo = this.changeOrder$.value?.change_order_no;

    const coPendingApprovalDate = this.changeOrder$.value?.create_date?.slice(0, 10);

    return currentTrial && org
      ? {
          fileName: `${currentTrial.short_name}_${org.name}_Change Order ${changeOrderNo}_Compare_${coPendingApprovalDate}.xlsx`,
        }
      : {};
  }

  getFilePath(org_id: string, co_id: string) {
    const trial_id = this.mainQuery.getValue().trialKey;

    return `trials/${trial_id}/vendors/${org_id}/changeorders/${co_id}/`;
  }

  @memo()
  isBtnLoading(str: string) {
    return this.btnLoading$.pipe(map((x) => x === str));
  }

  listMissingChangeOrderActivities(versionId: string): void {
    this.gqlService
      .listMissingChangeOrderActivities$(versionId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result?.data) {
          const hasMappingsOrActuals = result.data.reduce(
            (acc, activity) => {
              if (activity.has_invoice_mappings) acc.has_invoice_mappings.push(activity);

              if (activity.has_actuals) acc.has_actuals.push(activity);

              return acc;
            },
            {
              has_actuals: [] as listMissingChangeOrderActivitiesQuery[],
              has_invoice_mappings: [] as listMissingChangeOrderActivitiesQuery[],
            }
          );

          const extractSortedUnique = (
            arr: listMissingChangeOrderActivitiesQuery[],
            key: 'activity_name' | 'invoice_no'
          ) =>
            [...new Set(arr.map((activity) => activity[key]).filter(Boolean))]
              .sort((a, b) => Utils.localeAlphaNumSort(a || '', b || ''))
              .join(', ');

          this.invoicesWithMissedMapping = extractSortedUnique(
            hasMappingsOrActuals.has_invoice_mappings,
            'invoice_no'
          );
          this.activitiesWithMissedMapping = extractSortedUnique(
            hasMappingsOrActuals.has_invoice_mappings,
            'activity_name'
          );
          this.hasMissingActivitiesWithMapping =
            !!hasMappingsOrActuals.has_invoice_mappings?.length;
          this.hasMissingActivitiesWithActuals = !!hasMappingsOrActuals.has_actuals?.length;
        }
      });
  }

  async onApprove(force = false) {
    const co = this.changeOrder$.getValue()!;
    const org = await firstValueFrom(this.organization$.pipe(first()));

    if (!org) {
      return;
    }

    if (this.btnLoading$.getValue()) {
      return;
    }

    this.btnLoading$.next('approve');

    let resp!: CustomOverlayRef<
      boolean | undefined,
      { message: string },
      ConfirmationModalComponent
    >;

    if (force) {
      resp = this.overlayService.openPopup<
        { maxWidth: number; message: string },
        boolean | undefined,
        ConfirmationModalComponent
      >({
        content: ConfirmationModalComponent,
        data: {
          maxWidth: 460,
          message: `Selecting Force Approve will replace the current ${org.name} budget with Change Order ${co.change_order_no}. Do you want to proceed?`,
        },
        settings: {
          header: `Force Approve Change Order?`,
          primaryButton: {
            label: 'Force Approve',
          },
        },
      });
    } else {
      resp = this.overlayService.openPopup<
        { maxWidth: number; message: string },
        boolean | undefined,
        ConfirmationModalComponent
      >({
        content: ConfirmationModalComponent,
        data: {
          maxWidth: 460,
          message: `Selecting Approve will replace the current ${org.name} budget with Change Order ${co.change_order_no}. Do you want to proceed?`,
        },
        settings: {
          header: `Approve Change Order?`,
          primaryButton: {
            label: 'Approve',
          },
        },
      });
    }

    const event = await firstValueFrom(resp.afterClosed$);

    if (!event.data) {
      this.btnLoading$.next(false);

      return;
    }

    this.isApproveButtonDisableManually$.next(true);

    const { errors: approveErrors, success: approveSuccess } = await firstValueFrom(
      this.gqlService.approveRule$({
        activity_details: '{}',
        approval_type: ApprovalType.APPROVAL_CHANGE_ORDER,
        approved: true,
        comments: '',
        entity_id: co.id,
        entity_type: EntityType.CHANGE_ORDER,
        permission: 'PERMISSION_APPROVE_CHANGE_ORDER',
      })
    );

    if (!approveSuccess) {
      this.overlayService.error(approveErrors);
      this.btnLoading$.next(false);

      return;
    }

    const { data, errors, success } = await this.changeOrderService.approveChangeOrder({
      id: co.id,
      organization_id: co.organization_id,
    });

    this.mainStore.setProcessingLoadingState(EventType.CHANGE_ORDER_APPROVED, true);

    await firstValueFrom(
      this.eventService.processEvent$({
        entity_id: co.id,
        entity_type: EntityType.CHANGE_ORDER,
        payload: JSON.stringify({
          sendChangeOrderApprovedEmail: !force,
        }),
        type: EventType.CHANGE_ORDER_APPROVED,
      })
    );

    if (!(success && data)) {
      this.overlayService.error(errors);
    } else {
      if (this.launchDarklyService.flags$.getValue().change_order_success_modal) {
        this.overlayService.openPopup({
          content: ChangeOrderApprovedDialogComponent,
          data: { vendor_id: co.organization_id },
          settings: {
            header: '',
            hideHeader: true,
            primaryButton: { label: 'OK' },
            secondaryButton: { hidden: true },
          },
        });
      }
    }

    this.btnLoading$.next(false);
  }

  async onDecline() {
    const co = this.changeOrder$.getValue()!;

    if (this.btnLoading$.getValue()) {
      return;
    }

    this.btnLoading$.next('decline');

    const resp = this.overlayService.openPopup<
      { message: string },
      null | { reason: string },
      ConfirmationWithReasonModalComponent
    >({
      data: {
        message: `Are you sure you want to decline Change Order ${co.change_order_no}?`,
      },
      modal: ConfirmationWithReasonModalComponent,
      settings: {
        header: 'Decline Change Order?',
        primaryButton: {
          label: 'Decline',
        },
      },
    });

    const event = await firstValueFrom(resp.afterClosed$);

    if (!event.data?.reason) {
      this.btnLoading$.next(false);

      return;
    }

    const { errors: approveErrors, success: approveSuccess } = await firstValueFrom(
      this.gqlService.approveRule$({
        activity_details: '{}',
        approval_type: ApprovalType.APPROVAL_CHANGE_ORDER,
        approved: false,
        comments: '',
        entity_id: co.id,
        entity_type: EntityType.CHANGE_ORDER,
        permission: 'PERMISSION_APPROVE_CHANGE_ORDER',
      })
    );

    if (!approveSuccess) {
      this.overlayService.error(approveErrors);
      this.btnLoading$.next(false);

      return;
    }

    const { data, errors, success } = await this.changeOrderService.update({
      change_order_status: ChangeOrderStatus.STATUS_DECLINED,
      decline_reason: event.data.reason,
      id: co.id,
      organization_id: co.organization_id,
    });

    await firstValueFrom(
      this.eventService.processEvent$({
        entity_id: co.id,
        entity_type: EntityType.CHANGE_ORDER,
        type: EventType.CHANGE_ORDER_DECLINED,
      })
    );

    if (!(success && data)) {
      this.overlayService.error(errors);
    }

    this.btnLoading$.next(false);
  }

  async onDelete() {
    const co = this.changeOrder$.getValue();

    if (!co) {
      return;
    }

    if (this.btnLoading$.getValue()) {
      return;
    }

    this.btnLoading$.next('delete');

    const resp = this.overlayService.openPopup<
      { message: string },
      null | { reason: string },
      ConfirmationWithReasonModalComponent
    >({
      data: {
        message: `Are you sure you want to remove Change Order ${co.change_order_no}?`,
      },
      modal: ConfirmationWithReasonModalComponent,
      settings: {
        header: 'Remove Change Order',
        primaryButton: {
          label: 'Remove',
        },
      },
    });

    const event = await firstValueFrom(resp.afterClosed$);

    if (event.data?.reason) {
      const { success } = await this.changeOrderService.remove(co, event.data.reason);

      if (success) {
        void this.router.navigateByUrl(this.changeOrdersLink);
      }
    }

    this.btnLoading$.next(false);
  }

  async onDownloadCO() {
    const co = this.changeOrder$.getValue();

    if (!co) {
      return;
    }

    if (this.btnLoading$.getValue()) {
      return;
    }

    this.btnLoading$.next('download');
    const { data, errors, success } = await this.changeOrderQuery.downloadCO(co.id);

    if (success && data) {
      this.overlayService.success();
    } else {
      this.overlayService.error(errors);
    }

    this.btnLoading$.next(false);
  }

  async onDownloadLREBudget() {
    if (this.btnLoading$.getValue()) {
      return;
    }

    const vendor_id = this.changeOrder$.getValue()?.organization_id;

    if (!vendor_id) {
      return;
    }

    this.btnLoading$.next('lre-download');

    const { data, errors, success } = await this.lfuService.getTemplatePath({
      template_type: TemplateType.VENDOR_BUDGET_TEMPLATE,
      vendor_id,
    });

    if (!success) {
      this.overlayService.error(errors);
    }

    if (!(success && data)) {
      this.overlayService.error('There was a problem downloading the template');
    } else {
      await this.apiService.downloadFileFromPath(data.id);
    }

    this.btnLoading$.next(false);
  }

  async onEditChangeOrder() {
    const co = this.changeOrder$.getValue();

    if (!co) {
      return;
    }

    const result = this.overlayService.openPopup({
      data: { id: co.id, isVendorDisabled: !!this.toBudgetVersion() },
      modal: ChangeOrderUploadComponent,
      settings: {
        header: 'Edit Change Order',
      },
    });

    await firstValueFrom(result.afterClosed$);
  }

  onModalClose = async (ref: ReturnType<typeof this.activitiesModal.openModal>) => {
    if (!ref) {
      return;
    }

    const closeEvent = await firstValueFrom(ref.afterClosed$);
    const vendor_id = this.activitiesModal.vendor_id();

    if (closeEvent.data) {
      switch (closeEvent.data.type) {
        case 'close':
          break;
        case 'save': {
          const transaction_id = uuidv4();
          const budgetUpdated = await this.updateActivities(
            vendor_id,
            closeEvent.data.rows,
            closeEvent.data.isCategoryListChanged,
            closeEvent.data.temporaryCategories
          );

          let updateColumnsSuccessful = true;

          if (closeEvent.data.deletedColumns.length || closeEvent.data.renamedColumns.length) {
            updateColumnsSuccessful = await this.updateColumns({
              deletedColumns: closeEvent.data.deletedColumns,
              renamedColumns: closeEvent.data.renamedColumns,
              transaction_id,
              vendor_id,
            });
          }

          if (!budgetUpdated && updateColumnsSuccessful) {
            const tracking_id = uuidv4();

            // eslint-disable-next-line max-depth
            if (
              await this.eventService.triggerEvent({
                entity_id: vendor_id,
                entity_type: EntityType.ORGANIZATION,
                payload: JSON.stringify({
                  change_order_id: this.changeOrder()!.id,
                  createActivities: [],
                  deleteActivities: [],
                  updateActivities: [],
                }),
                tracking_id,
                transaction_id,
                type: EventType.INLINE_EDIT_CHANGE_ORDER,
              })
            ) {
              void this.eventTrackerService.trackEvent(tracking_id);
              this.overlayService.success('Budget update started');
            }
          }

          break;
        }
      }
    }
  };

  async onReplaceBudget() {
    if (this.btnLoading$.getValue()) {
      return;
    }

    const org = await firstValueFrom(this.organization$.pipe(first()));

    if (!org) {
      return;
    }

    const co = this.changeOrder$.getValue();

    if (!co) {
      return;
    }

    this.btnLoading$.next('replace');

    const modalResult = this.changeOrderBudgetUploadModal.open({
      change_order_id: co.id,
      change_order_status: co.change_order_status!,
      onSuccess: (processEventId) => {
        this.changeOrderBudgetTemplateUploadProgressId.set(processEventId);
      },
      organization_id: org.id,
    });

    await firstValueFrom(modalResult.afterClosed$);
    this.btnLoading$.next(false);
  }

  async onReturnToAdminReview() {
    const co = this.changeOrder$.getValue()!;

    if (this.btnLoading$.getValue()) {
      return;
    }

    this.btnLoading$.next('admin-review');

    const resp = this.overlayService.openPopup<
      { message: string; textAreaLabel: string },
      null | { reason: string },
      ConfirmationWithReasonModalComponent
    >({
      data: {
        message: `Are you sure you want to return Change Order ${co.change_order_no} to Pending Review?`,
        textAreaLabel: 'Reason for return to Admin Review',
      },
      modal: ConfirmationWithReasonModalComponent,
      settings: {
        header: 'Return to Pending Review?',
        primaryButton: {
          label: 'Return to Admin Review',
        },
      },
    });
    const event = await firstValueFrom(resp.afterClosed$);

    if (event.data?.reason) {
      const { data, errors, success } = await this.changeOrderService.update({
        admin_review_reason: event.data.reason,
        change_order_status: ChangeOrderStatus.STATUS_PENDING_REVIEW,
        id: co.id,
        organization_id: co.organization_id,
      });

      if (success && data) {
        this.overlayService.success();

        await firstValueFrom(
          this.eventService.processEvent$({
            entity_id: co.id,
            entity_type: EntityType.CHANGE_ORDER,
            type: EventType.CHANGE_ORDER_BACK_TO_PENDING_REVIEW,
          })
        );
      } else {
        this.overlayService.error(errors);
      }
    }

    this.btnLoading$.next(false);
  }

  async onSendApproval() {
    const co = this.changeOrder$.getValue();

    if (!co) {
      return;
    }

    if (this.btnLoading$.getValue()) {
      return;
    }

    this.btnLoading$.next('approval');

    const { data, errors, success } = await this.changeOrderService.update({
      change_order_status: ChangeOrderStatus.STATUS_PENDING_APPROVAL,
      id: co.id,
      organization_id: co.organization_id,
    });

    if (success && data) {
      this.overlayService.success();
    } else {
      this.overlayService.error(errors);
    }

    this.mainStore.setProcessingLoadingState(EventType.CHANGE_ORDER_PENDING_APPROVAL, true);

    await firstValueFrom(
      this.eventService.processEvent$({
        entity_id: co.id,
        entity_type: EntityType.CHANGE_ORDER,
        type: EventType.CHANGE_ORDER_PENDING_APPROVAL,
      })
    );

    this.btnLoading$.next(false);
  }

  async onSendingPendingApprovalReminder() {
    const co = this.changeOrder$.getValue()!;

    const { errors, success } = await firstValueFrom(
      this.eventService.processEvent$({
        entity_id: co.id,
        entity_type: EntityType.CHANGE_ORDER,
        type: EventType.CHANGE_ORDER_PENDING_APPROVAL,
      })
    );

    if (!success) {
      this.overlayService.error(errors);
    } else {
      this.overlayService.success(['Reminder email sent']);
    }
  }

  onTrackerIdChanged(trackerId: string) {
    this.changeOrderBudgetTemplateUploadProgressId.set(trackerId);

    if (!trackerId) {
      this.fetchChangeOrderBudget$.next(true);
    }
  }

  pathFn: () => string = () => '';

  sendForApprovalBtnTooltip(isApproveDisabled: boolean): string[] {
    if (this.hasMissingActivitiesWithActuals && this.hasMissingActivitiesWithMapping) {
      return [this.missingMappedActivitiesWithActualsSendForApprovalTooltip];
    }

    if (this.hasMissingActivitiesWithActuals) {
      return [this.missingActivitiesWithActualsSendForApprovalTooltip];
    }

    if (this.hasMissingActivitiesWithMapping) {
      return [this.missingWithMappingActivitiesSendForApprovalTooltip];
    }

    if (isApproveDisabled) {
      return [this.disableSendForApprovalButtonTooltip];
    }

    return [];
  }

  statusCheck(status: ChangeOrderStatus) {
    return this.changeOrder$.pipe(map((x) => x?.change_order_status === status));
  }

  // todo(AUXI-6706) this has almost same logic in budget too. Figure out if we should put them in modal injector
  async updateActivities(
    entity_id: string,
    rows: BeActivitiesAttributesModalRowData[],
    isCategoryListChanged: boolean,
    temporaryCategories: BeInlineCategoryDropdownOption[]
  ) {
    const trialId = this.mainQuery.getValue().trialKey;
    const trialVendorPath = `>${trialId}>${entity_id}`;
    const createdActivities: ActivityWithAttributes[] = [];
    const updatedActivities: ActivityWithAttributes[] = [];
    const deletedActivities: ActivityWithAttributes[] = [];

    rows.forEach((row) => {
      let activity_type: ActivityType;
      let activity_sub_type: ActivitySubType | undefined;
      let category_full_path: string;

      if (isCategoryListChanged) {
        category_full_path =
          temporaryCategories.find((category) => category.id === row.category)?.fullPath || '';
      } else {
        category_full_path =
          this.activitiesModal.categories()?.find((category) => category.id === row.category)
            ?.fullPath || '';
      }

      switch (row.activity_type) {
        case 'ACTIVITY_DISCOUNT':
        case 'ACTIVITY_INVESTIGATOR':
        case 'ACTIVITY_PASSTHROUGH':
        case 'ACTIVITY_SERVICE':
          activity_type = row.activity_type as ActivityType;
          break;
        case 'ACTIVITY_INVESTIGATOR_PATIENT_INVOICEABLES':
        case 'ACTIVITY_INVESTIGATOR_PATIENT_VISITS':
        case 'ACTIVITY_INVESTIGATOR_SITE_INVOICEABLES':
          activity_type = ActivityType.ACTIVITY_INVESTIGATOR;
          activity_sub_type = row.activity_type as ActivitySubType;
          break;
        default:
          activity_type = ActivityType.ACTIVITY_SERVICE;
          break;
      }

      const newData = {
        activity_sub_type,
        activity_type,
        attributes: Object.entries(row.attributes).map(([key, value]) => {
          return {
            attribute_name: key.startsWith('custom_attr_')
              ? decodeURIComponent(atob(key.split('custom_attr_')[1]))
              : key,
            attribute_value: value,
          };
        }) as BudgetActivityAttributes[],
        category_full_path: `${trialVendorPath}${category_full_path}`,
        category_id: row.category,
        display_label: row.display_label || '',
        id: row.id,
        name: row.activity_name || '',
        unit_cost: <number>row.unit_cost || 0,
        unit_num: <number>row.unit_num || 0,
        uom: row.uom || '',
      };

      if (row.deleted) {
        deletedActivities.push(newData);

        return;
      }

      if (!row.changed || !row.activity_name) {
        return;
      }

      if (row.isGenerated) {
        createdActivities.push({ ...newData, id: '' });
      } else {
        updatedActivities.push(newData);
      }
    });

    localStorage.setItem('anyEditActivities', 'true');

    if (
      createdActivities.length ||
      updatedActivities.length ||
      deletedActivities.length ||
      isCategoryListChanged
    ) {
      const categories: { [key: string]: AuxBudgetCategoryData[] } = {};

      if (isCategoryListChanged) {
        const filteredCategories: BeInlineCategoryDropdownOption[] = temporaryCategories
          .map((category, index) => ({
            ...category,
            sourceIndex: index + 2,
          }))
          .filter((category) => {
            return (
              category.categoryType !== CategoryType.CATEGORY_INVESTIGATOR &&
              category.categoryType !== CategoryType.CATEGORY_DISCOUNT &&
              category.fullPath !== ''
            );
          });

        filteredCategories.forEach((category) => {
          categories[`/${category.categoryType}/${trialVendorPath}${category.fullPath}`] = [
            {
              __typename: 'AuxBudgetCategoryData',
              attributes: '[{}]',
              categories: '[{}]',
              category_type: category.categoryType || CategoryType.CATEGORY_SERVICE,
              direct_expenses_note: '',
              display_label: '',
              display_order: 0,
              full_path: `${trialVendorPath}${category.fullPath}`,
              item_count: 0,
              //fields with default values
              item_label: '0',
              item_order: 0,
              name: category.name,
              parent_path: `${trialVendorPath}${category.path}`,
              previous_full_path: category.isRenamed
                ? this.getCategoryPreviousFullPath(category, trialVendorPath)
                : '', // only populate if the name of the category was updated
              source_index: category.sourceIndex || 0,
              trial_id: trialId,
              vendor_id: entity_id || '',
            },
          ];
        });
      }

      const tracking_id = uuidv4();

      if (
        await this.eventService.triggerEvent({
          entity_id,
          entity_type: EntityType.ORGANIZATION,
          payload: JSON.stringify({
            categories,
            change_order_id: this.changeOrder()!.id,
            createActivities: createdActivities,
            deleteActivities: deletedActivities,
            updateActivities: updatedActivities,
          }),
          tracking_id,
          type: EventType.INLINE_EDIT_CHANGE_ORDER,
        })
      ) {
        void this.eventTrackerService.trackEvent(tracking_id);
        this.overlayService.success('Budget update started');
      }

      return true;
    }

    return false;
  }

  async updateColumns({
    deletedColumns,
    renamedColumns,
    transaction_id,
    vendor_id,
  }: {
    deletedColumns: string[];
    renamedColumns: { newValue: string; oldValue: string }[];
    transaction_id: string;
    vendor_id: string;
  }) {
    for (const name of deletedColumns) {
      await firstValueFrom(
        this.gqlService.removeBudgetAttribute$({
          budget_type: BudgetType.BUDGET_CHANGE_ORDER,
          change_order_id: this.changeOrder$.getValue()?.id,
          name,
          transaction_id,
          vendor_id,
        })
      );
    }

    for (const { newValue, oldValue } of renamedColumns) {
      const { errors } = await firstValueFrom(
        this.gqlService.renameBudgetAttribute$({
          budget_type: BudgetType.BUDGET_CHANGE_ORDER,
          change_order_id: this.changeOrder$.getValue()?.id,
          name: oldValue,
          new_name: newValue,
          transaction_id,
          vendor_id,
        })
      );

      if (errors.length) {
        this.overlayService.error(String(errors));

        return false;
      }
    }

    return true;
  }
}
